import { useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';
import { ResponseWrapper } from 'ui/toast';
import { useDispatch } from 'react-redux';
import { addNegativeToast, removeAllToasts } from 'modules/toastContainer';
import noOp from 'utils/noop';

import {
  CreateSnippetMutationResult,
  GraphQLSnippet,
  SnippetFieldsInput,
  SnippetValidationFailure,
  ValidationData,
} from '../types';
import { getValidationData } from '../utils';

export type Variables = {
  name: string;
  destinationProjectId?: number;
  fields: SnippetFieldsInput;
};

export const CloneSnippetMutation = gql`
  mutation CloneSnippet(
    $name: String!
    $destinationProjectId: Long!
    $fields: SnippetFieldsInput!
  ) {
    cloneSnippet(name: $name, destinationProjectId: $destinationProjectId, fields: $fields) {
      success {
        content
        description
        name
        positionalParameters
        projectId
      }
      failure {
        __typename
        ... on FieldInvalidFailure {
          field
          errorType {
            __typename
            ... on AnchorTagsWithJS {
              errorMsg
            }
            ... on HasCycle {
              errorMsg
            }
            ... on HasScriptTag {
              errorMsg
            }
            ... on IframeTagWithJS {
              errorMsg
            }
            ... on IllegalCharacters {
              errorMsg
            }
            ... on NotRenderable {
              errorMsg
            }
          }
        }
        ... on FieldNotProvidedFailure {
          field
        }
        ... on FieldTooLongFailure {
          field
        }
        ... on SnippetCreationFailure {
          errorMsg
        }
        ... on SnippetIdDoesNotExistFailure {
          Id
        }
        ... on SnippetNameAlreadyExistsFailure {
          field
        }
        ... on SnippetParameterDuplicateFailure {
          field
        }
      }
    }
  }
`;

type Config = {
  onComplete: () => void;
  onError: (errors: ValidationData[]) => void;
  onSuccess: (snippet: GraphQLSnippet) => void;
};

export function useCloneSnippet({ onComplete = noOp, onError = noOp, onSuccess = noOp }: Config) {
  const dispatch = useDispatch();
  const [cloneSnippet, data] = useMutation<CreateSnippetMutationResult, Variables>(
    CloneSnippetMutation,
  );

  useEffect(() => {
    // don't do anything until the mutation gets called
    if (!data.called) {
      return;
    }

    if (!data.loading && data.error) {
      dispatch(addNegativeToast('An unknown error occurred')); // check Network tab (possible change to GraphQL schema)
    }

    if (!data.loading && data?.data?.cloneSnippet) {
      const { failure, success } = data.data.cloneSnippet;
      const errors: ValidationData[] = [];
      const failures =
        (Array.isArray(failure) || failure === null || failure === undefined
          ? failure
          : [failure]) || [];

      failures.forEach((err: SnippetValidationFailure) => {
        const validationData = getValidationData(err);

        dispatch(addNegativeToast(<ResponseWrapper>{validationData.errorMsg}</ResponseWrapper>));
        errors.push(validationData);
      });
      if (errors.length) {
        onError(errors);
      }

      if (success) {
        dispatch(removeAllToasts());
        onSuccess(success);
      }

      // call the complete callback
      onComplete();
    }
  }, [data.loading]);

  return [cloneSnippet, data] as const;
}
